import { SessionPersistence } from '../util/persistence';
import base64url from 'base64url';
import { doStartSession } from './startSession';
import {
  doLoginFailure, doLogout, doSetInitialTempCode,
  doSetStateToken, setServerState, doStartAuthSessionAndRestoreAuthState
} from './shared';
import { AuthenticationProtocols } from '../constants/clientProtocols';
import { decideNextStep } from './decideNextStep';
import * as types from '../constants/cidraActions';
import { setInitialUsername } from './index';

const STATE_TOKEN_STORAGE_PREFIX = 'st:';
const ACCOUNT_TOKEN_STORAGE_PREFIX = 'at:';
const STATE_TOKEN_KEY_REGEX = /^st:.*/;

/**
 * Attempt to delete any expired state and account tokens.
 */
export function queueStateTokenDeletion() {
  setTimeout(deleteExpiredTokens, 0);

  function deleteExpiredTokens() {
    try {
      let keys = SessionPersistence.getKeys();

      for (let key of keys) {
        if (key.match(STATE_TOKEN_KEY_REGEX) ) {
          const jwt = SessionPersistence.get(key);

          if (!isJwtValid(jwt) ) {
            SessionPersistence.remove(key);
            SessionPersistence.remove(key.replace(STATE_TOKEN_STORAGE_PREFIX, ACCOUNT_TOKEN_STORAGE_PREFIX) );
          }
        }
      }
    }
    catch (ex) {
      console.error(ex);
    }
  }
}

function isJwtValid(jwt) {
  if (!jwt || jwt.split('.').length < 3)
    return false;

  const nowDate = new Date();

  const jwtChunks = jwt.split('.');
  const jwtPayload = jwtChunks[1];
  const decodedPayload = base64url.decode(jwtPayload);
  const payloadObj = JSON.parse(decodedPayload);
  const expirationTime = payloadObj.exp;

  const expDate = new Date(expirationTime * 1000);
  return nowDate < expDate;
}

export function storeAuthenticationState(stateToken) {
  return function(dispatch, getState) {
    const jwtChunks = stateToken.split('.');
    const jwtBody = jwtChunks[1];
    const decodedJwtBody = base64url.decode(jwtBody);
    const jti = JSON.parse(decodedJwtBody).jti;
    doSaveStateToken(jti, stateToken);

    const {
      accountToken, cloudauthEnvironment, selfServiceEnvironment, username,
    } = getState().login;
    const decodedAccountToken = JSON.parse(atob(accountToken) );
    const updatedAccountToken = btoa(JSON.stringify({
      ... decodedAccountToken,
      cloudauthEnvironment,
      selfServiceEnvironment,
      username,
    }) );
    doSaveAccountToken(jti, updatedAccountToken);
  };
}

function doSaveStateToken(jti, stateToken) {
  const key = STATE_TOKEN_STORAGE_PREFIX + jti;
  doSaveToken(key, stateToken);
}

function doSaveAccountToken(jti, accountToken) {
  const key = ACCOUNT_TOKEN_STORAGE_PREFIX + jti;
  doSaveToken(key, accountToken);
}

function doSaveToken(key, accountToken) {
  try {
    SessionPersistence.set(key, accountToken);
  }
  catch (ex) {
    console.error(ex);
  }
}

export function doGetStateToken(jti) {
  const key = STATE_TOKEN_STORAGE_PREFIX + jti;
  return doGetToken(key);
}

export function doGetAccountToken(jti) {
  const key = ACCOUNT_TOKEN_STORAGE_PREFIX + jti;
  return doGetToken(key);
}

function doGetToken(key) {
  try {
    const token = SessionPersistence.get(key);
    SessionPersistence.remove(key);
    return token;
  }
  catch (ex) {
    console.error(ex);
  }
}

export function doStartAuthenticationSession(jti) {
  return function (dispatch) {
    const accountToken = doGetAccountToken(jti);
    const stateToken = doGetStateToken(jti);
    if (accountToken && stateToken && isJwtValid(stateToken) ) {
      const decodedAccountToken = JSON.parse(atob(accountToken) );
      dispatch(setInitialUsername(decodedAccountToken.username) );
      dispatch(doSetStateToken(stateToken) );
      dispatch(doStartSession(
        accountToken, decodedAccountToken.cloudauthEnvironment,
        decodedAccountToken.selfServiceEnvironment) );
    }
    else {
      dispatch(doLogout() );
    }
  };
}

export function doStartResumeAuthSession() {
  return doStartAuthSessionAndRestoreAuthState(
    setRestoreAuthSessionStarted, doRestoreAuthStateFromUncompletedAuthSession);
}

/**
 * Restore authentication state from an uncompleted authentication session based on state token.
 */
function doRestoreAuthStateFromUncompletedAuthSession() {
  return function(dispatch, getState) {
    const {
      authClient, oneSignSessionId, stateToken,
    } = getState().login;
    const { AuthResponseAuthErrorContinued, AuthResumeAuthSession, } = AuthenticationProtocols;
    const resumeAuthFlow = new AuthResumeAuthSession(oneSignSessionId, stateToken);
    return authClient.sendRequest(resumeAuthFlow).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode && serverState.responseType !== AuthResponseAuthErrorContinued) {
        dispatch(doLogout() );
        dispatch(doLoginFailure(serverState.errorCode) );
      }
      else {
        dispatch(doRestoreAuthSessionSuccess() );
        dispatch(doSetInitialTempCode(serverState.temporaryCodeAllowed && serverState.temporaryCodeAvailable) );
        dispatch(decideNextStep(serverState, false) );
      }
    }).catch(ex => {
      dispatch(doLogout() );
      dispatch(doLoginFailure(ex) );
    });
  };
}

function setRestoreAuthSessionStarted() {
  return { type: types.RESTORE_AUTH_SESSION_STARTED, };
}

function doRestoreAuthSessionSuccess() {
  return { type: types.RESTORE_AUTH_SESSION_SUCCESS, };
}
